/** Activity: deposits, withdrawals and swaps with status, network, tx hash and explorer link; filters. */
import {
  Address,
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  Input,
  Modal,
  Notice,
  Segmented,
  Select,
  Skeleton,
  formatAmount,
  formatDateTime,
  Spot,
} from "@custos/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowDownToLine, ArrowLeftRight, ArrowUpFromLine } from "lucide-react";
import { useMemo, useState } from "react";
import { PageHeader } from "../components/AppShell";
import { ExternalLink } from "../components/bits";
import {
  CANCELLABLE,
  EMPTY_FILTER,
  depositStatus,
  filterActivity,
  mergeActivity,
  swapStatus,
  withdrawalStatus,
  type ActivityFilter,
  type ActivityItem,
} from "../lib/activity";
import { api } from "../lib/api";
import { assetKey, findChain, networkLabel } from "../lib/chains";
import { errorMessage } from "../lib/errors";
import { qk, useConfig, useDeposits, useSwaps, useWithdrawals } from "../lib/queries";

export function ActivityPage() {
  const config = useConfig();
  const deposits = useDeposits();
  const withdrawals = useWithdrawals();
  const swaps = useSwaps();
  const [filter, setFilter] = useState<ActivityFilter>(EMPTY_FILTER);

  const all = useMemo(
    () => mergeActivity(deposits.data ?? [], withdrawals.data ?? [], swaps.data ?? []),
    [deposits.data, withdrawals.data, swaps.data],
  );
  const items = useMemo(() => filterActivity(all, filter), [all, filter]);
  const error = deposits.error ?? withdrawals.error ?? swaps.error;

  return (
    <>
      <PageHeader title="Activity" subtitle="Deposits, withdrawals and swaps on your account." />
      <Card className="mb-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-[auto_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)] lg:items-end">
          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-fg">Type</span>
            <Segmented
              label="Type"
              value={filter.type}
              onChange={(type) => setFilter({ ...filter, type })}
              options={[
                { value: "all", label: "All" },
                { value: "deposit", label: "Deposits" },
                { value: "withdrawal", label: "Withdrawals" },
                { value: "swap", label: "Swaps" },
              ]}
            />
          </div>
          <Field label="Asset">
            {(p) => (
              <Select
                {...p}
                value={filter.asset}
                onChange={(e) => setFilter({ ...filter, asset: e.target.value })}
              >
                <option value="">All assets</option>
                {(config.data?.assets ?? []).map((a) => (
                  <option key={assetKey(a.chain, a.symbol)} value={assetKey(a.chain, a.symbol)}>
                    {a.symbol} · {networkLabel(findChain(config.data?.chains ?? [], a.chain), a.chain)}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label="From">
            {(p) => (
              <Input
                {...p}
                type="date"
                value={filter.from}
                max={filter.to || undefined}
                onChange={(e) => setFilter({ ...filter, from: e.target.value })}
              />
            )}
          </Field>
          <Field label="To">
            {(p) => (
              <Input
                {...p}
                type="date"
                value={filter.to}
                min={filter.from || undefined}
                onChange={(e) => setFilter({ ...filter, to: e.target.value })}
              />
            )}
          </Field>
        </div>
        {(filter.type !== "all" || filter.asset || filter.from || filter.to) && (
          <Button variant="ghost" size="sm" className="mt-3" onClick={() => setFilter(EMPTY_FILTER)}>
            Clear filters
          </Button>
        )}
      </Card>
      {error ? (
        <Notice tone="danger">{errorMessage(error)}</Notice>
      ) : (
        <ActivityList
          items={items}
          loading={deposits.isPending || withdrawals.isPending || swaps.isPending}
        />
      )}
    </>
  );
}

/** Cards on phone/tablet, a table on desktop. */
export function ActivityList({ items, loading }: { items: ActivityItem[]; loading: boolean }) {
  const config = useConfig();
  const [cancelId, setCancelId] = useState<string | null>(null);
  const chains = config.data?.chains ?? [];

  if (loading) {
    return (
      <Card className="flex flex-col gap-3">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-14" />
        ))}
      </Card>
    );
  }
  if (items.length === 0) {
    return (
      <Card>
        <EmptyState icon={<Spot name="history" />} title="Nothing here yet">
          Deposits, withdrawals and swaps will show up here.
        </EmptyState>
      </Card>
    );
  }

  return (
    <>
      {/* Phone + tablet */}
      <ul className="stagger flex flex-col gap-2 lg:hidden" data-testid="activity-cards">
        {items.map((item) => {
          const s = status(item);
          return (
            <li key={`${item.kind}-${item.id}`} data-testid="activity-item">
              <Card className="p-3 sm:p-4">
                <div className="flex items-start gap-3">
                  <KindIcon kind={item.kind} />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
                      <p className="font-medium text-fg">{KIND_LABEL[item.kind]}</p>
                      <AmountText item={item} />
                    </div>
                    <p className="text-xs text-muted">
                      {networkLabel(findChain(chains, item.chain), item.chain)} · {formatDateTime(item.at)}
                    </p>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <Badge tone={s.tone} title={s.help}>
                        {s.label}
                      </Badge>
                      <TxLink item={item} />
                      {item.kind === "withdrawal" && CANCELLABLE.includes(item.data.status) && (
                        <Button variant="ghost" size="sm" onClick={() => setCancelId(item.id)}>
                          Cancel
                        </Button>
                      )}
                    </div>
                    {item.kind === "withdrawal" && item.data.failureReason && (
                      <p className="mt-1 text-xs text-danger">{item.data.failureReason}</p>
                    )}
                  </div>
                </div>
              </Card>
            </li>
          );
        })}
      </ul>

      {/* Desktop */}
      <Card className="hidden overflow-x-auto p-0 lg:block lg:p-0">
        <table className="w-full text-sm" data-testid="activity-table">
          <thead>
            <tr className="border-b border-line text-left text-xs text-muted">
              <th className="px-4 py-3 font-medium">Type</th>
              <th className="px-4 py-3 font-medium">Amount</th>
              <th className="px-4 py-3 font-medium">Network</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium">Date</th>
              <th className="px-4 py-3 font-medium">Transaction</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {items.map((item) => {
              const s = status(item);
              return (
                <tr
                  key={`${item.kind}-${item.id}`}
                  className="border-b border-line last:border-0"
                  data-testid="activity-row"
                >
                  <td className="px-4 py-3">
                    <span className="flex items-center gap-2 text-fg">
                      <KindIcon kind={item.kind} small />
                      {KIND_LABEL[item.kind]}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <AmountText item={item} />
                  </td>
                  <td className="px-4 py-3 text-muted">
                    {networkLabel(findChain(chains, item.chain), item.chain)}
                  </td>
                  <td className="px-4 py-3">
                    <Badge tone={s.tone} title={s.help}>
                      {s.label}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-muted">{formatDateTime(item.at)}</td>
                  <td className="px-4 py-3">
                    <TxLink item={item} />
                  </td>
                  <td className="px-4 py-3 text-right">
                    {item.kind === "withdrawal" && CANCELLABLE.includes(item.data.status) && (
                      <Button variant="ghost" size="sm" onClick={() => setCancelId(item.id)}>
                        Cancel
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>

      <CancelModal id={cancelId} onClose={() => setCancelId(null)} />
    </>
  );
}

export const KIND_LABEL: Record<ActivityItem["kind"], string> = {
  deposit: "Deposit",
  withdrawal: "Withdrawal",
  swap: "Swap",
};

export function status(item: ActivityItem) {
  if (item.kind === "deposit") return depositStatus(item.data);
  if (item.kind === "swap") return swapStatus(item.data);
  return withdrawalStatus(item.data);
}

function AmountText({ item }: { item: ActivityItem }) {
  if (item.kind === "swap") {
    return (
      <p className="font-mono text-sm text-fg tabular-nums">
        −{formatAmount(item.data.fromAmount, 8)} <span className="text-muted">{item.data.fromAsset}</span> → +
        {formatAmount(item.data.toAmount, 8)} <span className="text-muted">{item.data.toAsset}</span>
      </p>
    );
  }
  return (
    <p className="font-mono text-sm text-fg tabular-nums">
      {item.kind === "deposit" ? "+" : "−"}
      {formatAmount(item.amount, 8)} <span className="text-muted">{item.asset}</span>
    </p>
  );
}

export function KindIcon({ kind, small }: { kind: ActivityItem["kind"]; small?: boolean }) {
  const Icon = kind === "deposit" ? ArrowDownToLine : kind === "swap" ? ArrowLeftRight : ArrowUpFromLine;
  return (
    <span
      aria-hidden
      className={
        (small ? "size-7" : "size-9") +
        " inline-flex shrink-0 items-center justify-center rounded-full " +
        (kind === "deposit"
          ? "bg-success/15 text-success"
          : kind === "swap"
            ? "bg-accent/15 text-accent"
            : "bg-primary-soft text-primary-strong")
      }
    >
      <Icon className="size-4" />
    </span>
  );
}

function TxLink({ item }: { item: ActivityItem }) {
  const hash = item.data.txHash;
  const url = item.data.explorerUrl;
  if (item.kind === "swap" && !hash) {
    return (
      <span className="text-xs text-muted">
        {item.data.simulated ? "In-account (testnet)" : "In-account"}
      </span>
    );
  }
  if (!hash) return <span className="text-xs text-muted">Not sent yet</span>;
  return (
    <span className="inline-flex min-w-0 items-center gap-1">
      <Address value={hash} copy={false} />
      {url && (
        <ExternalLink href={url} className="text-xs">
          Explorer
        </ExternalLink>
      )}
    </span>
  );
}

function CancelModal({ id, onClose }: { id: string | null; onClose: () => void }) {
  const queryClient = useQueryClient();
  const cancel = useMutation({
    mutationFn: (withdrawalId: string) => api.cancelWithdrawal(withdrawalId),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: qk.withdrawals }),
        queryClient.invalidateQueries({ queryKey: qk.balances }),
      ]);
      onClose();
    },
  });
  return (
    <Modal
      open={id !== null}
      onOpenChange={(open) => {
        if (!open) {
          cancel.reset();
          onClose();
        }
      }}
      title="Cancel this withdrawal?"
      description="The amount and fee go back to your balance. This only works before it's signed."
    >
      <div className="flex flex-col gap-3">
        {cancel.error && <Notice tone="danger">{errorMessage(cancel.error)}</Notice>}
        <Button variant="danger" block loading={cancel.isPending} onClick={() => id && cancel.mutate(id)}>
          Yes, cancel withdrawal
        </Button>
        <Button variant="secondary" block onClick={onClose}>
          Keep it
        </Button>
      </div>
    </Modal>
  );
}
